import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import {
  MessageSquare,
  Users,
  Target,
  Star,
  BarChart3,
  Eye,
  ArrowRight,
  CheckCircle,
  Zap,
  Crown,
  PlusIcon,
  ArrowDown,
  Search,
  TrendingDown,
  CreditCard,
  MessageCircle,
  Sparkles,
  ShieldCheck,
  Clock
} from 'lucide-react';
import { EyeSlashIcon,  PiggyBankIcon, SmileyMeltingIcon, XIcon } from '@phosphor-icons/react';
import { WaitlistForm } from '@/components/WaitlistForm';
import { useWaitlistCount } from '@/hooks/useWaitlistCount';
import { CookieBanner } from '@/components/CookieBanner';
import { DemoSection } from '@/components/landing/DemoSection';
import { ValuePropsSection } from '@/components/landing/ValuePropsSection';
import { StatsSection } from '@/components/landing/StatsSection';
import { BeforeAfterSection } from '@/components/landing/BeforeAfterSection';

const features = [
  {
    icon: MessageSquare,
    title: "Visual notes",
    description: "Capture thoughts as they happen — No more losing context in messy text docs."
  },
  {
    icon: Star,
    title: "Recognise patterns",
    description: "See what users actually want across interviews — Stop guessing which features matter most."
  },
  {
    icon: Target,
    title: "Clear next steps",
    description: "Transform feedback into prioritized build decisions — Know exactly what to do next.",
  },
  {
    icon: BarChart3,
    title: "AI-Powered Analysis",
    description: "Find themes instantly, without weeks of tagging — Intelligence that actually works.",
    premium: true
  },
  {
    icon: Eye,
    title: "Live Guidance",
    description: "Ask better questions in real-time — Never miss important follow-ups again.",
    premium: true
  },
  {
    icon: Users,
    title: "Team Collaboration",
    description: "Share insights instantly with your team — Everyone sees the same patterns.",
    premium: true
  },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: {
      type: "spring" as const,
      stiffness: 100,
      damping: 15
    }
  }
};

const Landing = () => {
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  const { count: waitlistCount, loading: countLoading, setCount: setWaitlistCount } = useWaitlistCount();

  const problemRef = useRef(null);
  const featuresRef = useRef(null);
  const pricingRef = useRef(null);
  const faqRef = useRef(null);
  const ctaRef = useRef(null);

  const isProblemInView = useInView(problemRef, { once: true, margin: "-100px" });
  const isFeaturesInView = useInView(featuresRef, { once: true, margin: "-100px" });
  const isPricingInView = useInView(pricingRef, { once: true, margin: "-100px" });
  const isFaqInView = useInView(faqRef, { once: true, margin: "-100px" });
  const isCtaInView = useInView(ctaRef, { once: true, margin: "-100px" });

  const section = window.location.hash;

  useEffect(() => {
    if (section) {
      const element = document.getElementById(section.replace('#', ''));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [section]);

  return (
    <div className="min-h-screen bg-gradient-dynamic">
      {/* Navigation */}
      <nav className="bg-card/50 border-b border-border px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-50 backdrop-blur-sm">
        <div className="pl-4 pr-4 flex items-center justify-between space-x-4">
          <div className='flex items-center'>
            <img className='w-16 h-16' src='/yak-stak-logo.png' alt='YakStak Logo - User Research Platform' />
            <div className="text-2xl font-bold text-foreground self-center">
              YakStak
            </div>
          </div>
          {/* Desktop nav */}
          <div className="hidden sm:flex items-center space-x-4">
            <Button variant='ghost' onClick={() => navigate('#features')}>How it works</Button>
            <Button variant='ghost' onClick={() => navigate('#pricing')}>Pricing</Button>
            {/* <Button variant="ghost" onClick={() => navigate('/auth')}>
              Login
            </Button> */}
            <Button onClick={() => navigate('#cta')}>
              Get early access
            </Button>
          </div>
          {/* Mobile nav toggle */}
          <button
            className="sm:hidden p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Open navigation menu"
            onClick={() => setNavOpen(v => !v)}
          >
            <svg className="w-7 h-7 text-gray-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
        {/* Mobile nav dropdown */}
        {navOpen && (
          <div className="sm:hidden bg-card border-b border-border px-4 pt-2 pb-4 min-h-48 flex flex-col gap-2 shadow-xl animate-fade-in-down absolute left-0 right-0 top-full z-60">
            <Button
              variant='outline'
              className="justify-start w-full text-base"
              onClick={() => { setNavOpen(false); navigate('#features'); }}
            >
              How it works
            </Button>
            <Button
              variant='outline'
              className="justify-start w-full text-base"
              onClick={() => { setNavOpen(false); navigate('#pricing'); }}
            >
              Pricing
            </Button>
            {/* <Button variant="outline" className="justify-start w-full text-base" onClick={() => { setNavOpen(false); navigate('/auth'); }}>Login</Button> */}
            <Button
              className="w-full text-base"
              onClick={() => { setNavOpen(false); navigate('#cta'); }}
            >
              Get early access
            </Button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id='hero' className="px-4 sm:px-6 py-12 sm:py-20 bg-gradient-subtle" role="banner">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div 
            className='flex flex-col gap-8 justify-center items-center'
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.h1 
              className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground mb-6" 
              itemProp="headline"
              variants={fadeInUp}
            >
              <motion.span 
                className="block mb-2"
                variants={fadeInUp}
              >
                Stop guessing what to build with
              </motion.span>

              <motion.span 
                className="relative inline-block"
                variants={fadeInUp}
              >
                <motion.span 
                  className="text-foreground text-5xl sm:text-6xl md:text-7xl font-extrabold"
                  variants={scaleIn}
                >
                  YakStak
                </motion.span>
                <motion.span 
                  className="aria-hidden highlighter-effect animate pointer-events-none absolute -bottom-8 sm:-bottom-4 h-16 -left-1 -right-1"
                />
              </motion.span>
            </motion.h1>

            <motion.span variants={fadeInUp}>
              <motion.p 
                className="text-lg sm:text-xl text-foreground/90 max-w-3xl mx-auto mt-6 sm:mt-8 font-medium"
                variants={fadeInUp}
              >
                Turn user interviews into clear product decisions — Fast.
              </motion.p>
              <motion.p 
                className="text-base sm:text-xl text-muted-foreground max-w-3xl mx-auto"
                variants={fadeInUp}
              >
                Interview insights in seconds, not hours.
              </motion.p>
            </motion.span>

            <motion.div 
              className="flex flex-col w-full max-w-sm mx-auto gap-4"
              variants={fadeInUp}
            >
              <motion.span variants={fadeInUp}>
                <p className='text-lg text-foreground font-medium'>Built for founders, by founders</p>
              </motion.span>
              
              <motion.div 
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button 
                  size="lg" 
                  onClick={() => navigate('#cta')} 
                  className="text-xl sm:text-2xl px-6 sm:px-8 py-3 w-full bg-accent text-accent-foreground hover:bg-accent/90"
                >
                  Get early access
                  <ArrowRight className="ml-2" strokeWidth={4} />
                </Button>
              </motion.div>

              <motion.div 
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  size='lg'
                  variant='outline'
                  className="text-xl sm:text-2xl bg-primary hover:bg-primary text-primary-foreground font-medium w-full max-w-xs self-center"
                  onClick={() => {
                    const el = document.getElementById('features');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  How it works
                  <motion.div
                    animate={{ y: [0, -5, 0] }}
                    transition={{ 
                      repeat: Infinity, 
                      duration: 2, 
                      ease: "easeInOut",
                      delay: 1
                    }}
                  >
                    <ArrowDown className="ml-2" strokeWidth={4} />
                  </motion.div>
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Demo Video Section */}
      <DemoSection />

      {/* Value Props Section */}
      <ValuePropsSection />

      {/* Stats Highlight */}
      <StatsSection />

      {/* Before After Demo Section */}
      <BeforeAfterSection />

      {/* Problem Section - Sticky Notes */}
      <section id="problem" ref={problemRef} className="px-4 sm:px-6 pt-8 pb-4 bg-card/50" aria-labelledby="problem-heading">
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-12" variants={staggerContainer} initial="hidden" animate={isProblemInView ? "visible" : "hidden"}>
            <motion.span className='flex flex-col gap-0 mb-8' variants={staggerContainer}>
              <motion.h2 id="problem-heading" className="text-3xl font-bold text-foreground" variants={fadeInUp}>
                We felt this struggle
              </motion.h2>
              <motion.p className="text-xl font-medium text-foreground" variants={fadeInUp}>So we built YakStak</motion.p>
            </motion.span>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 min-h-[12rem] sm:min-h-[10rem]'>
              <motion.div className={`sticky-note p-6 transition-all duration-500`} variants={fadeInUp} transition={{ delay: 0.1 }}>
                <div className="text-center flex flex-col justify-center h-full">
                  <Search className='w-12 h-12 mx-auto mb-3 text-gray-700' />
                  <h4 className="text-xl font-bold text-gray-800 mb-2">Hidden blockers</h4>
                  <p className="text-sm font-semibold text-gray-700">Real problems stay buried in scattered notes.</p>
                </div>
              </motion.div>
              <motion.div className={`sticky-note p-6 transition-all duration-500`} variants={fadeInUp} transition={{ delay: 0.2 }}>
                <div className="text-center flex flex-col justify-center h-full">
                  <TrendingDown className="w-12 h-12 mx-auto mb-3 text-gray-700" />
                  <h4 className="text-xl font-bold text-gray-800 mb-2">Slowed growth</h4>
                  <p className="text-sm font-semibold text-gray-700">No insights means no idea why users don't convert.</p>
                </div>
              </motion.div>
              <motion.div className={`sticky-note p-6 transition-all duration-500`} variants={fadeInUp} transition={{ delay: 0.3 }}>
                <div className="text-center flex flex-col justify-center h-full">
                  <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-700" />
                  <h4 className="text-xl font-bold text-gray-800 mb-2">Revenue leaks</h4>
                  <p className="text-sm font-semibold text-gray-700">
                    Bad interviews hide what's stopping payments.
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section id='features' ref={featuresRef} className="px-4 sm:px-6 py-12 sm:py-16 md:scroll-mt-20 bg-gradient-subtle" aria-labelledby="features-heading">
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-12" variants={staggerContainer} initial="hidden" animate={isFeaturesInView ? "visible" : "hidden"}>
            <motion.h2 id="features-heading" className="text-3xl font-bold text-foreground mb-4" variants={fadeInUp}>
              Turn customer conversations into product decisions
            </motion.h2>
            <motion.p className="text-lg text-muted-foreground/90" variants={fadeInUp}>
              Everything you need to be a successful founder
            </motion.p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  initial="hidden"
                  animate={isFeaturesInView ? "visible" : "hidden"}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    className={`hover:shadow-lg transition-all duration-500 border-0 bg-card/80 relative hover-scale`}
                  >
                    {feature.premium && (
                      <Badge className="absolute top-4 right-4 bg-gradient-brand text-primary-foreground">
                        <Crown className="w-3 h-3 mr-1" />
                        Pro
                      </Badge>
                    )}
                    <CardHeader>
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle className="text-xl text-foreground">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>


      {/* Pricing Section */}
      <section id='pricing' ref={pricingRef} className="px-4 sm:px-6 py-12 sm:py-16 bg-card/50 md:scroll-mt-10" aria-labelledby="pricing-heading">
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-12" initial="hidden" animate={isPricingInView ? "visible" : "hidden"} variants={staggerContainer}>
            <motion.h2 id="pricing-heading" className="text-3xl font-bold text-foreground mb-4" variants={fadeInUp}>
              Early access pricing
            </motion.h2>
            <motion.p className="text-lg text-muted-foreground" variants={fadeInUp}>
              Start free and upgrade when you need advanced features
            </motion.p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <motion.div variants={fadeInUp} initial="hidden" animate={isPricingInView ? "visible" : "hidden"} transition={{ delay: 0.1 }}>
              <Card className={`relative transition-all duration-500 hover-scale`}>
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl text-foreground">Free</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-foreground">£0</span>
                    <span className="text-muted-foreground ml-2">/forever</span>
                  </div>
                  <p className="text-muted-foreground mt-2">Perfect for solo founders starting out</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-success mr-3 flex-shrink-0" />
                      <span className="text-foreground">Unlimited note taking</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-success mr-3 flex-shrink-0" />
                      <span className="text-foreground">Basic sticky note organisation</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-success mr-3 flex-shrink-0" />
                      <span className="text-foreground">Up to 3 interview spaces</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-success mr-3 flex-shrink-0" />
                      <span className="text-foreground">2 AI analyses per month</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-success mr-3 flex-shrink-0" />
                      <span className="text-foreground">Manual insights</span>
                    </li>
                  </ul>
                  <Button
                    className='w-full mt-6 hover-scale'
                    variant='outline'
                    onClick={() => navigate('#cta')}
                  >
                    Get started free
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={fadeInUp} initial="hidden" animate={isPricingInView ? "visible" : "hidden"} transition={{ delay: 0.2 }}>
              <Card className={`relative ring-2 ring-primary shadow-lg transition-all duration-500 hover-scale`}>
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground">
                  Most Popular
                </Badge>
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl text-foreground">Pro</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-foreground">£5</span>
                    <span className="text-muted-foreground ml-2">/month</span>
                  </div>
                  <p className="text-muted-foreground mt-2">Stop guessing what to build</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-success mr-3 flex-shrink-0" />
                      <span className="text-foreground">25 AI-powered insights per month</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-success mr-3 flex-shrink-0" />
                      <span className="text-foreground">Team members can view notespaces</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-success mr-3 flex-shrink-0" />
                      <span className="text-foreground">Unlimited interview spaces</span>
                    </li>
                    <li className="flex items-center">
                      <div className='flex flex-row gap-3 text-center'>
                        <PlusIcon strokeWidth='3' className='w-5 h-5 flex-shrink-0 text-foreground' />
                        <span className="text-foreground">Everything in Free</span>
                      </div>
                    </li>
                  </ul>
                  <Button
                    className='w-full mt-6 hover-scale'
                    variant='default'
                    onClick={() => navigate('#cta')}
                  >
                    Become a Pro
                    <Zap className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={fadeInUp} initial="hidden" animate={isPricingInView ? "visible" : "hidden"} transition={{ delay: 0.3 }}>
              <Card className={`relative transition-all duration-500 hover-scale`}>
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl text-foreground">Expert</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-foreground">£30</span>
                    <span className="text-muted-foreground ml-2">/month</span>
                  </div>
                  <p className="text-muted-foreground mt-2">Never ask the wrong questions again</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-success mr-3 flex-shrink-0" />
                      <span className="text-foreground">Real-time interview guidance</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-success mr-3 flex-shrink-0" />
                      <span className="text-foreground">Team collaboration</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-success mr-3 flex-shrink-0" />
                      <span className="text-foreground">Priority support</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-success mr-3 flex-shrink-0" />
                      <span className="text-foreground">Advanced integrations</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-success mr-3 flex-shrink-0" />
                      <span className="text-foreground">Import and export features</span>
                    </li>
                    <li className="flex items-center">
                      <div className='flex flex-row gap-3 text-center'>
                        <PlusIcon strokeWidth='3' className='w-5 h-5 flex-shrink-0 text-foreground' />
                        <span className="text-foreground">Everything in Pro</span>
                      </div>
                    </li>
                  </ul>
                  <Button
                    className='w-full mt-6 hover-scale'
                    variant='default'
                    onClick={() => navigate('#cta')}
                  >
                    Become an expert
                    <Crown className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" ref={faqRef} className="px-4 sm:px-6 py-12 sm:py-16 bg-gradient-subtle" aria-labelledby="faq-heading">
        <div className="max-w-4xl mx-auto">
          <motion.div className="text-center mb-12" variants={staggerContainer} initial="hidden" animate={isFaqInView ? "visible" : "hidden"}>
            <motion.h2 id="faq-heading" className="text-3xl font-bold text-foreground mb-4" variants={fadeInUp}>
              Frequently Asked Questions
            </motion.h2>
            <motion.p className="text-lg text-muted-foreground" variants={fadeInUp}>
              Everything you need to know about YakStak
            </motion.p>
          </motion.div>
          <motion.div variants={fadeInUp} initial="hidden" animate={isFaqInView ? "visible" : "hidden"} transition={{ delay: 0.2 }}>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-left">What is YakStak and how does it work?</AccordionTrigger>
                <AccordionContent>
                  YakStak is a visual note-taking platform designed specifically for user research and customer interviews. It allows you to capture thoughts as sticky notes, organize them visually, and use AI to identify patterns and insights across multiple interviews. Simply create a note space, add your interview notes, and let our AI help you discover what users really want.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger className="text-left">Do I need technical skills to use YakStak?</AccordionTrigger>
                <AccordionContent>
                  Not at all! YakStak is designed for founders, product managers, and researchers of all technical backgrounds. The interface is intuitive and visual - if you can use sticky notes, you can use YakStak. Our AI features work automatically in the background to provide insights without any setup required.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger className="text-left">What's the difference between the pricing plans?</AccordionTrigger>
                <AccordionContent>
                  We have three plans: <strong>Free (£0/forever)</strong> - unlimited note-taking, up to 3 interview spaces, and 2 AI analyses per month, perfect for solo founders. <strong>Pro (£5/month)</strong> - adds 25 AI insights per month, team viewing access, and unlimited spaces. <strong>Expert (£30/month)</strong> - includes real-time interview guidance, full team collaboration, priority support, and advanced integrations for scaling teams.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4">
                <AccordionTrigger className="text-left">How does the AI analysis work?</AccordionTrigger>
                <AccordionContent>
                  Our AI analyzes your interview notes to identify patterns, themes, and insights across multiple conversations. It can spot contradictions, highlight frequently mentioned pain points, and suggest areas for deeper investigation. The AI is trained specifically for user research and helps you move from scattered notes to actionable product decisions quickly.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-5">
                <AccordionTrigger className="text-left">Can I collaborate with my team?</AccordionTrigger>
                <AccordionContent>
                  Yes! Pro plan users can share interview spaces with team members for real-time collaboration. Multiple people can add notes, organize insights, and view AI analysis simultaneously. This makes it perfect for product teams, research teams, or any group working together on user insights.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-6">
                <AccordionTrigger className="text-left">Is my data secure?</AccordionTrigger>
                <AccordionContent>
                  Absolutely. We take data security seriously and use industry-standard encryption to protect your research data. Your interview notes and insights are stored securely and are only accessible to you and team members you explicitly invite to collaborate.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-7">
                <AccordionTrigger className="text-left">When will YakStak be available?</AccordionTrigger>
                <AccordionContent>
                  We're currently in early access development and working closely with founding users to build the perfect solution. Join our waitlist to get notified when we launch and to help shape the product with your feedback. Early access users will also get special pricing and priority support.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" ref={ctaRef} className="px-4 sm:px-6 py-12 sm:py-20 bg-gradient-subtle relative overflow-hidden">
        {/* Yakky mascot in background */}
        <div className="absolute top-4 right-4">
          <img src='/confused-yakky.png' alt='Yakky' className='w-24 h-24 transform rotate-12' />
        </div>

        <div className="max-w-4xl mx-auto relative z-10">
          <motion.div className="text-center mb-8" initial="hidden" animate={isCtaInView ? "visible" : "hidden"} variants={staggerContainer}>
            <motion.h2 className="text-4xl font-bold text-primary-accent mb-6" variants={fadeInUp}>
              Ready to stop guessing what users want?
            </motion.h2>
            <motion.p className="text-xl text-primary-accent/90 mb-8" variants={fadeInUp}>
              Join us in early access and help us build the perfect solution for you
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" animate={isCtaInView ? "visible" : "hidden"} variants={fadeInUp} transition={{ delay: 0.4 }}>
            {/* Waitlist Counter */}
            <div className="text-center mb-6">
              {!countLoading && (
                <div className="inline-flex items-center gap-3 text-base font-semibold">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-success rounded-full animate-pulse shadow-md"></div>
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-foreground">
                    <span className="text-xl font-bold text-primary">{waitlistCount}</span> founders already in
                  </span>
                </div>
              )}
            </div>
              <WaitlistForm onSubmit={() => {
                setWaitlistCount((prev) => prev + 1);
              }} />
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-muted/50 bg-gradient-surface px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className='flex justify-center space-x-2 mb-4'>
              <img className='w-16 h-16' src='/yak-stak-logo.png' alt='YakStak Logo' />
              <div className="text-2xl font-bold text-foreground self-center">
                YakStak
              </div>
            </div>
            <p className="mb-6">Stack your thoughts and insights with YakStak</p>
            
            {/* Footer Links */}
            <div className="flex justify-center gap-8 mb-8">
              <button
                onClick={() => navigate("/resources")}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Resources
              </button>
              <button
                onClick={() => navigate("/blog")}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Blog
              </button>
            </div>
          </div>
          
          <div className="border-t border-border pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4">
              <div className="flex flex-wrap gap-4 text-sm">
                <a href="/cookie-policy" className="text-muted-foreground hover:text-primary transition-colors">
                  Cookie Policy
                </a>
                <a href="/privacy-policy" className="text-muted-foreground hover:text-primary transition-colors">
                  Privacy Policy
                </a>
                <a href="/terms" className="text-muted-foreground hover:text-primary transition-colors">
                  Terms of Service
                </a>
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm">&copy; 2025 YakStak. All rights reserved.</p>
              <p className="text-xs text-muted-foreground mt-2">
                Built with{" "}
                <a 
                  href="http://buildpad.io/?via=kieran" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Buildpad
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>
      <CookieBanner />
    </div>
  );
};

export default Landing;
